<template>
    <div
        class="tl-input-file-upload"
        :class="{
            'tl-input-file-upload-disabled': disabled,
            'tl-input-file-upload-invalid': Object.values(modelState).some(f => !!f.error)
        }"
        data-testid="input-file-upload"
    >
        <label
            v-if="label"
            :for="inputId"
            class="tl-input-file-upload-label"
        >
            {{ label }}
        </label>

        <div
            v-if="loading"
            class="tl-input-file-upload-loader"
        >
            <LoadingCircle large />
        </div>

        <input
            :id="inputId"
            ref="fileInputRef"
            class="file-input"
            type="file"
            :accept="acceptedExtensions.map(ext => `.${ext}`).join(',')"
            multiple
            :disabled="disabled"
            @click="fileInputRef!.value = ''"
            @change="handleFileSelect"
        />

        <button
            type="button"
            class="tl-input-drop-zone no-user-select"
            :disabled="disabled"
            @dragenter.prevent
            @dragover.prevent
            @drop="handleDrop"
            @click="handleDropZoneClick"
        >
            <i class="tl-input-drop-zone-icon mi-cloud-upload" />
            <p>{{ t(dropZoneTranslationKey, { maxFileSize: maxFileSizeMB, extensions: displayedExtensions }) }}</p>
        </button>

        <div
            v-if="Object.values(modelState).some(f => f.progress > 0) || props.progress > 0"
            class="tl-input-file-upload-summary"
        >{{ summary }}</div>

        <transition-group
            name="list-fade"
            tag="ul"
            class="tl-files-list"
            data-testid="input-file-upload-file-list"
        >
            <li
                v-for="(file, index) in modelState"
                :key="index"
                class="tl-files-list-row"
            >
                <span class="tl-files-list-element">
                    <transition-group
                        name="fade"
                        tag="span"
                        class="tl-files-list-element-icons"
                    >
                        <LoadingCircle
                            v-if="file.loading"
                            key="circle"
                        />
                        <i
                            v-if="file.error?.length"
                            key="error"
                            class="mi-warning error"
                        />
                        <i
                            v-if="!file.error?.length && file.verified"
                            key="verified"
                            class="mi-check success"
                        />
                    </transition-group>

                    <button
                        v-if="file.resource"
                        class="tl-files-list-element-resource-name"
                        type="button"
                        data-testid="file-name"
                        :disabled="disabled"
                        @click="emit('download-document', { ...file.resource, componentId: id })"
                    >{{ file.name }}</button>

                    <span
                        v-else
                        class="tl-files-list-element-name"
                        data-testid="file-name"
                    >{{ file.name }}</span>

                    <button
                        v-if="!disabled"
                        class="tl-input-files-remove"
                        type="button"
                        data-testid="file-delete"
                        @click.stop="removeFile(index, file)"
                    >
                        <i
                            class="mi-close"
                        />
                    </button>
                </span>

                <span
                    v-show="0 < file.progress && file.progress < 1"
                    class="tl-input-file-upload-progress"
                    :style="{ width: file.progress * 100 + '%' }"
                />

                <ErrorText
                    v-if="file.error?.length"
                    :error="file.error"
                />
            </li>
        </transition-group>
        <span
            v-show="0 < progress && progress < 1"
            class="tl-input-file-upload-progress"
            :style="{ width: progress * 100 + '%' }"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, useId } from 'vue'
import useTools from 'widget-components/utilities/tools'
import { useI18n } from 'widget-components/utilities/i18n'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import LoadingCircle from 'widget-components/components/LoadingCircle/LoadingCircle.vue'
import type { InputDocumentItem } from 'widget-components/components/InputFileUpload/types'
import { isEmpty, isNil, uniq } from 'ramda'
import { useTouchScreen } from 'widget-components/composables/touch-screen'
import { useBreakpoints } from 'widget-components/composables/breakpoints'

const { sm } = useBreakpoints()
const { isTouchScreen } = useTouchScreen()

const { toBase64 } = useTools()
const { t } = useI18n()

const props = withDefaults(defineProps<{
    id?: string
    modelValue: Record<string, InputDocumentItem>
    initialValue?: Record<string, InputDocumentItem>
    loading?: boolean
    disabled?: boolean
    maxFileSize: number
    extensions: string
    progress?: number
    error?: string
    label?: string
    title?: string
    uploadLimit?: string | number
}>(), {
    modelValue: () => ({}),
    maxFileSize: 10 * 1024 * 1024, // 10MB
    extensions: 'jpg,jpeg,png',
    progress: 0,
    error: '',
})

const emit = defineEmits([
    'update:modelValue',
    'error',
    'file:add',
    'file:remove',
    'download-document',
])

const filesError = computed(() => {
    if (props.uploadLimit) {
        const maxFilesCount = Number(props.uploadLimit)
        if (Object.values(modelState.value).length > maxFilesCount) {
            return t('input-file-upload.error.upload-limit', { maxFilesCount })
        }
    }

    const filesError = Object.values(modelState.value).find(file => file.error?.length)?.error

    return filesError || ''
})

const inputId = computed(() => {
    return props.id ? `${props.id}-input` : useId()
})

onMounted(() => {
    watch(filesError, () => {
        emit('error', filesError.value || '')
    }, { immediate: true })
})

const summary = computed(() => {
    const filesCount = Object.keys(modelState.value).length

    if (!filesCount) return ''

    const { filesUploaded, isUploadRunning } = Object.values(modelState.value).reduce((res, file) => {
        if (
            0 < file.progress && file.progress < 1 ||
            0 < props.progress && props.progress < 1
        ) {
            res.isUploadRunning = true
        }
        if (file.uploaded || file.progress > 0 || props.progress > 0) {
            res.filesUploaded ++
        }
        return res
    }, { filesUploaded: 0, isUploadRunning: false })

    const files = `${filesUploaded}/${filesCount}`

    return isUploadRunning
        ? t('input-file-upload.status.uploading', { files })
        : t('input-file-upload.status.uploaded', { files })
})

const fileInputRef = ref<HTMLInputElement>()

const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)

const label = computed(() => props.label)

const maxFileSizeMB = computed(() => (props.maxFileSize / 1024 / 1024).toFixed(2))

const loadFile = async (file: File) => {
    const item = modelState.value[file.name]

    const data = isNil(item.data)
        ? ''
        : await toBase64(file)

    modelState.value = {
        ...modelState.value,
        [file.name]: { ...item, data },
    }

    emit('file:add', modelState.value[file.name])
}

const maxFilesCount = computed(() => Number(props.uploadLimit))

const getNormalizedExtension = (fileName: string) => fileName.split('.').pop()!.trim().toLowerCase()

const JPG_ALIASES = ['jpeg', 'jpe', 'jfif']

const configuredExtensions = computed(() => uniq(
    props.extensions.split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean),
))

const acceptedExtensions = computed(() => configuredExtensions.value.includes('jpg')
    ? uniq([...configuredExtensions.value, ...JPG_ALIASES])
    : configuredExtensions.value,
)

const displayedExtensions = computed(() => uniq(
    configuredExtensions.value.flatMap(ext => ext === 'jpg' ? ['jpg', 'jpeg'] : [ext]),
).join(', ').toUpperCase())

const validateFile = (file: InputDocumentItem, index: number) => {
    const errors = []
    const extension = getNormalizedExtension(file.name)

    // skip extension validation for default files, because sometimes they don't contain extension
    const isNotDefaultFile = isNil(file.resource) || isEmpty(file.resource)

    if (isNotDefaultFile && !acceptedExtensions.value.includes(extension)) {
        errors.push(
            t('input-file-upload.error.type', { types: displayedExtensions.value }),
        )
        file.data = ''
    }

    if (isNotDefaultFile && (file.size ?? 0) > props.maxFileSize) {
        errors.push(t('input-file-upload.error.size', { size: `${ props.maxFileSize / 1024 / 1024 }MB` }))
        file.data = ''
    }

    if (index >= maxFilesCount.value) {
        errors.push(t('input-file-upload.error.upload-limit', { maxFilesCount: maxFilesCount.value }))
    }

    file.error = errors.join(', ')
}

const addFile = async (file: File) => {
    const nameParts = file.name.split('.')
    nameParts[nameParts.length - 1] = getNormalizedExtension(file.name)

    const item = {
        name: nameParts.join('.'),
        size: file.size,
        data: '',
        error: '',
    } as InputDocumentItem

    validateFile(item, Object.values(modelState.value).length)
    modelState.value = {
        ...modelState.value,
        [file.name]: item,
    }
    await loadFile(file)
}

const removeFile = (key: string, file: InputDocumentItem) => {
    delete modelState.value[key]
    Object.values(modelState.value).forEach((item, index) => {
        validateFile(item, index)

        const itemWithUpload = item as InputDocumentItem & { uploadedData?: { requireVirusCheck?: boolean } }
        if (itemWithUpload.uploadedData?.requireVirusCheck) {
            itemWithUpload.uploadedData.requireVirusCheck = false
        }
    })
    emit('file:remove', file)
}

const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    Array
        .from(event.dataTransfer!.files)
        .forEach((file) => addFile(file))
}

const dropZoneTranslationKey = computed(() => (sm.value && isTouchScreen)
    ? 'input-file-upload.drop-zone-mobile'
    : 'input-file-upload.drop-zone')

const handleDropZoneClick = () => {
    fileInputRef.value!.value = ''
    fileInputRef.value!.click()
}

const handleFileSelect = async (event: Event) => {
    Array
        .from((event.target as HTMLInputElement).files || [])
        .forEach(async (file) => await addFile(file))
}

onMounted(() => {
    if (props.initialValue) {
        modelState.value = props.initialValue
    }
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
}, { deep: true })

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
}, { deep: true })

</script>

<style scoped lang="sass">
@import './InputFileUpload.sass'
</style>
